import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import _ from 'lodash';
import Pagination from 'reactjs-hooks-pagination';
import { Tab, Tabs } from 'react-bootstrap';

import Breadcrumb from '../../components/Breadcrumb';
import { updateThresholdCredit, userListService, updateShowCredit, createUserService } from '../../services/userService';
import { getCouponById, updateUserCoupon } from "../../services/couponService";
import { createHashSHA256, getDisplayMessage } from '../../helper';
import UsersTable from './UsersTable';
import {
    buildUserListApiQuery,
    buildUserListUrlSearch,
    countActiveFilters,
    getActiveFilterChips,
    getNextSortState,
    parseUserListQuery,
} from './userListQuery';

const EMPTY_FILTERS = {
    status: '',
    company: '',
    createdFrom: '',
    createdTo: '',
    updatedFrom: '',
    updatedTo: '',
    verifiedUser: '',
    verifiedEmail: '',
};

const Users = () => {
    const history = useHistory();
    const location = useLocation();
    const debounceRef = useRef(null);
    const skipSearchDebounceRef = useRef(true);
    const initialQuery = useMemo(() => parseUserListQuery(location.search), []);

    const [list, setList] = useState([]);
    const [loader, setLoader] = useState(true);
    const [listError, setListError] = useState('');
    const [limit] = useState(10);
    const [page, setPage] = useState(initialQuery.page);
    const [type, setType] = useState(initialQuery.type);
    const [search, setSearch] = useState(initialQuery.search);
    const [searchInput, setSearchInput] = useState(initialQuery.search);
    const [sortBy, setSortBy] = useState(initialQuery.sortBy);
    const [sortOrder, setSortOrder] = useState(initialQuery.sortOrder);
    const [status, setStatus] = useState(initialQuery.status);
    const [company, setCompany] = useState(initialQuery.company);
    const [createdFrom, setCreatedFrom] = useState(initialQuery.createdFrom);
    const [createdTo, setCreatedTo] = useState(initialQuery.createdTo);
    const [updatedFrom, setUpdatedFrom] = useState(initialQuery.updatedFrom);
    const [updatedTo, setUpdatedTo] = useState(initialQuery.updatedTo);
    const [verifiedUser, setVerifiedUser] = useState(initialQuery.verifiedUser);
    const [verifiedEmail, setVerifiedEmail] = useState(initialQuery.verifiedEmail);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
    const [currentId, setCurrentId] = useState('');
    const [percentOff, setPercentOff] = useState('');
    const [couponId, setCouponId] = useState('');
    const [email, setEmail] = useState('');
    const [couponIdErr, setCouponIdErr] = useState('');
    const [isDisabled, setIsDisabled] = useState('');
    const [createModal, setCreateModal] = useState('');
    const [addUserModal, setAddUserModal] = useState(false);
    const [fullName, setFullName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [phone, setPhone] = useState('');
    const [fullNameErr, setFullNameErr] = useState('');
    const [companyNameErr, setCompanyNameErr] = useState('');
    const [userEmailErr, setUserEmailErr] = useState('');
    const [passwordErr, setPasswordErr] = useState('');
    const [confirmPasswordErr, setConfirmPasswordErr] = useState('');
    const [addUserErr, setAddUserErr] = useState('');
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [pagination, setPagination] = useState('');
    const defaultTab = type === 1 ? '2' : '1';

    const listState = useMemo(() => ({
        page,
        search,
        type,
        sortBy,
        sortOrder,
        status,
        company,
        createdFrom,
        createdTo,
        updatedFrom,
        updatedTo,
        verifiedUser,
        verifiedEmail,
    }), [
        page, search, type, sortBy, sortOrder, status, company,
        createdFrom, createdTo, updatedFrom, updatedTo, verifiedUser, verifiedEmail,
    ]);

    const activeFilterCount = useMemo(() => countActiveFilters(listState), [listState]);
    const activeFilterChips = useMemo(() => getActiveFilterChips(listState), [listState]);

    const breadcrumb = [
        { link: '', linkText: 'Users' }
    ];

    const syncUrl = useCallback((nextState) => {
        const nextSearch = buildUserListUrlSearch(nextState);
        const current = `${location.pathname}${location.search || ''}`;
        const target = `${location.pathname}${nextSearch}`;
        if (current !== target) {
            history.replace(target);
        }
    }, [history, location.pathname, location.search]);

    const getList = useCallback((overrideState) => {
        const queryState = overrideState || listState;
        setLoader(true);
        setListError('');
        const params = buildUserListApiQuery(queryState);

        userListService(params).then((res) => {
            setLoader(false);
            const rows = res.data && (res.data.userList || res.data.list);
            if (Array.isArray(rows)) {
                setList(rows);
                setPagination({
                    totalCount: (res.data && res.data.totalCount) || rows.length || 0,
                    currentPage: Number(res.data && res.data.currentPage) || 1,
                    pageSize: Number(res.data && res.data.pageSize) || 10,
                    totalPages: (res.data && res.data.totalPages) || 0,
                });
                setListError('');
            } else {
                setList([]);
                setPagination('');
                setListError(getDisplayMessage(res.data, 'Unable to load users'));
            }
        });
    }, [listState]);

    const refreshList = useCallback(() => {
        getList(listState);
    }, [getList, listState]);

    useEffect(() => {
        getList(listState);
        syncUrl(listState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        type,
        search,
        sortBy,
        sortOrder,
        status,
        company,
        createdFrom,
        createdTo,
        updatedFrom,
        updatedTo,
        verifiedUser,
        verifiedEmail,
    ]);

    useEffect(() => {
        if (skipSearchDebounceRef.current) {
            skipSearchDebounceRef.current = false;
            return undefined;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            const nextSearch = searchInput.trim();
            setPage(1);
            setSearch(nextSearch);
        }, 500);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchInput]);

    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSort = (columnKey) => {
        const nextSort = getNextSortState(sortBy, sortOrder, columnKey);
        setSortBy(nextSort.sortBy);
        setSortOrder(nextSort.sortOrder);
        setPage(1);
    };

    const openFilterModal = () => {
        setFilterDraft({
            status: status || '',
            company: company || '',
            createdFrom: createdFrom || '',
            createdTo: createdTo || '',
            updatedFrom: updatedFrom || '',
            updatedTo: updatedTo || '',
            verifiedUser: verifiedUser || '',
            verifiedEmail: verifiedEmail || '',
        });
        setFilterModalOpen(true);
    };

    const applyFilters = () => {
        setStatus(filterDraft.status || '');
        setCompany(filterDraft.company || '');
        setCreatedFrom(filterDraft.createdFrom || '');
        setCreatedTo(filterDraft.createdTo || '');
        setUpdatedFrom(filterDraft.updatedFrom || '');
        setUpdatedTo(filterDraft.updatedTo || '');
        setVerifiedUser(filterDraft.verifiedUser || '');
        setVerifiedEmail(filterDraft.verifiedEmail || '');
        setPage(1);
        setFilterModalOpen(false);
    };

    const resetFilters = () => {
        setFilterDraft(EMPTY_FILTERS);
        setStatus('');
        setCompany('');
        setCreatedFrom('');
        setCreatedTo('');
        setUpdatedFrom('');
        setUpdatedTo('');
        setVerifiedUser('');
        setVerifiedEmail('');
        setPage(1);
        setFilterModalOpen(false);
    };

    const removeFilterChip = (chipKey) => {
        if (chipKey === 'createdRange') {
            setCreatedFrom('');
            setCreatedTo('');
            setPage(1);
            return;
        }
        if (chipKey === 'updatedRange') {
            setUpdatedFrom('');
            setUpdatedTo('');
            setPage(1);
            return;
        }
        if (chipKey === 'status') setStatus('');
        if (chipKey === 'company') setCompany('');
        if (chipKey === 'verifiedUser') setVerifiedUser('');
        if (chipKey === 'verifiedEmail') setVerifiedEmail('');
        setPage(1);
    };

    const handleSelect = (e, userId, selectType, threshold) => {
        setCouponIdErr('');

        let params = {};
        if (selectType === 1) {
            params = {
                type: 3,
                thresholdValue: e.target.value,
                usrId: userId,
            };
        } else if (selectType === 4) {
            params = {
                type: 4,
                isActiveSubscriber: threshold === 0 ? 1 : 0,
                usrId: userId,
            };
        } else if (selectType === 5) {
            params = {
                custId: userId,
                showCredit: !threshold,
            };
        } else {
            params = {
                type: 2,
                thresholdRefill: threshold === 1 ? 2 : 1,
                usrId: userId,
            };
        }

        const _service = selectType === 5 ? updateShowCredit : updateThresholdCredit;
        _service(params).then((res) => {
            if (res.status) {
                refreshList();
            }
        });
    };
    const handleClose = () => {
        setCreateModal(false);
        setCouponId();
        setCouponIdErr();
        setCurrentId('');
        setEmail('');
    }

    const handleCloseAddUser = () => {
        setAddUserModal(false);
        setFullName('');
        setCompanyName('');
        setUserEmail('');
        setPassword('');
        setConfirmPassword('');
        setCountryCode('');
        setPhone('');
        setFullNameErr('');
        setCompanyNameErr('');
        setUserEmailErr('');
        setPasswordErr('');
        setConfirmPasswordErr('');
        setAddUserErr('');
        setAddUserLoading(false);
    }

    const handleValidateAddUser = () => {
        let validate = true;
        const emailPattern = /(([a-zA-Z0-9\-?\.?]+)@(([a-zA-Z0-9\-_]+\.)+)([a-z]{2,3}))+$/;

        if (!fullName || !fullName.trim()) {
            setFullNameErr('Full name is required');
            validate = false;
        } else {
            setFullNameErr('');
        }

        if (!companyName || !companyName.trim()) {
            setCompanyNameErr('Company name is required');
            validate = false;
        } else {
            setCompanyNameErr('');
        }

        if (!userEmail || !userEmail.trim()) {
            setUserEmailErr('Email is required');
            validate = false;
        } else if (!new RegExp(emailPattern).test(userEmail.trim().toLowerCase())) {
            setUserEmailErr('Invalid email');
            validate = false;
        } else {
            setUserEmailErr('');
        }

        if (!password) {
            setPasswordErr('Password is required');
            validate = false;
        } else {
            setPasswordErr('');
        }

        if (!confirmPassword) {
            setConfirmPasswordErr('Confirm password is required');
            validate = false;
        } else if (password !== confirmPassword) {
            setConfirmPasswordErr('Password and Confirm Password needs to be same');
            validate = false;
        } else {
            setConfirmPasswordErr('');
        }

        return validate;
    }

    const handleAddUserSubmit = async (e) => {
        e.preventDefault();
        setAddUserErr('');

        if (!handleValidateAddUser()) {
            return;
        }

        setAddUserLoading(true);
        const hashedPassword = await createHashSHA256(password);
        const params = {
            fullName: fullName.trim(),
            companyName: companyName.trim(),
            email: userEmail.trim().toLowerCase(),
            password: hashedPassword,
            confirmPassword: hashedPassword,
            acceptedTerms: true,
            verifiedUser: 1,
            verifiedEmail: 1,
            companyDetails: {
                companyName: companyName.trim(),
            },
        };

        if (countryCode && countryCode.trim()) {
            params.countryCode = countryCode.trim();
        }
        if (phone && phone.trim()) {
            params.phone = Number(phone.trim());
        }

        createUserService(params).then(res => {
            setAddUserLoading(false);
            if (res.status) {
                handleCloseAddUser();
                refreshList();
            } else {
                setAddUserErr(getDisplayMessage(res.data, 'Unable to create user'));
            }
        });
    }

    const handleValidate = () => {
        let validate = true;
        // if (couponId == '' || couponId == undefined) {
        //     setCouponIdErr('Coupon name is required');
        //     validate = false
        // }

        // if (percentOff == '' || percentOff == null || percentOff == undefined) {
        //     validate = false
        //
        // }


        return validate;
    }

    const handleOnChange = (e,) => {
        setCouponIdErr('')
        setPercentOff('')
        console.log("fffffffffffff", currentId, e.target.value)

        setCouponId(e.target.value);
        // let couponId=e.target.value
        let params = `?couponId=${e.target.value}`;

        if (e.target.value) {
            getCouponById(params).then(res => {
                if (res.status) {

                    console.log(res.data.list, "dfffffffffffffxcccccccccc")
                    console.log(res, "sasdfdsddsffffffffffffffxcccccccccc")

                    if (res.data.list.errors && res.data.list.errors.raw.message) {
                        setCouponIdErr(res.data.list.errors.raw.message)
                    } else {

                        if (!res.data.list.valid) {
                            setCouponIdErr("This coupon code is expired.")

                        }
                        else {
                            console.log(res.data.list, "nbvbnbvdfffffffffffffxcccccccccc")




                            if (!_.isEmpty(res.data.list.metadata && res.data.list.metadata.emails)) {
                                // setCouponIdErr(res.data.list.metadata.emails)
                                console.log(res.data.list, "1nbvbnbvdfffffffffffffxcccccccccc")

                                let emails = (res.data.list.metadata.emails)
                                emails = emails.split(",")
                                var n = emails.includes(email);

                                console.log(n, "fffffffffffffffffffsfds")
                                if (n) {
                                    setPercentOff(res.data.list.percent_off)
                                } else {
                                    setPercentOff('')
                                    setCouponIdErr("This coupon code is not valid for you")
                                }
                            } else {
                                console.log(res.data.list, "2nbvbnbvdfffffffffffffxcccccccccc")
                                setPercentOff(res.data.list.percent_off)


                            }
                        }
                    }

                }
            })
        }
    }
    const handleSubmit = e => {
        e.preventDefault();
        if (handleValidate()) {
            setIsDisabled(true);
            let params = {};
            // if (currentId) {            }
            if (currentId) {

                params = {

                    'percentOff': percentOff ? percentOff : 0,
                    'couponId': couponId ? couponId : '',
                    'usrId': currentId ? currentId : '',
                    'showCoupon': couponId ? 2 : 1,

                }
            }


            updateUserCoupon(params).then(res => {
                setCurrentId('')
                setIsDisabled(false);
                if (res.status) {
                    handleClose();
                    refreshList();
                }
            })
        }

    }
    const getTabValue = (tabKey) => {
        const nextType = String(tabKey) === '2' ? 1 : 0;
        if (type === nextType) {
            return;
        }
        setType(nextType);
        setPage(1);
    };

    const handlePageChange = (nextPageValue) => {
        const nextPage = Number(nextPageValue) || 1;
        if (nextPage === page) {
            return;
        }
        setPage(nextPage);
    };

    return (
        <div className="page_wrapper">
            <div className="search_bar d-flex flex-wrap align-items-center">
                <div className="flex-grow-1 position-relative mr-2 mb-2">
                    <input
                        type="text"
                        maxLength={"100"}
                        className="form-control"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        placeholder="Search by name, email, mobile, company, or user ID" />
                    <i className="fa fa-search barsearch" />
                </div>
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mb-2 mr-2"
                    onClick={openFilterModal}
                >
                    Filters {activeFilterCount ? `(${activeFilterCount})` : ''}
                </button>
                {searchInput ? (
                    <button
                        type="button"
                        className="btn btn-link btn-sm mb-2"
                        onClick={() => setSearchInput('')}
                    >
                        Clear search
                    </button>
                ) : ''}
            </div>

            {activeFilterChips.length ? (
                <div className="px-lg-4 pb-2 d-flex flex-wrap align-items-center">
                    {activeFilterChips.map((chip) => (
                        <span key={chip.key} className="badge badge-light border mr-2 mb-2 p-2">
                            {chip.label}
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ml-2"
                                onClick={() => removeFilterChip(chip.key)}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <button type="button" className="btn btn-link btn-sm mb-2" onClick={resetFilters}>
                        Clear all filters
                    </button>
                </div>
            ) : ''}

            <Breadcrumb breadcrumb={breadcrumb} />

            {listError ? (
                <div className="alert alert-danger mx-lg-4" role="alert">
                    {listError}
                </div>
            ) : ''}

            <div className="d-flex justify-content-between align-content-center page_header">
                <h2>Users List</h2>
                <span className="btn btn-primary" onClick={() => setAddUserModal(true)}>Add User</span>
            </div>
            <div className="">

                <div className="px-lg-4 pt-3 pb-md-5 pb-4">

                    <Tabs
                        activeKey={defaultTab}
                        onSelect={(e) => {
                            if (e == null) {
                                return;
                            }
                            getTabValue(e);
                        }}
                        className="breadcrumb-account nav-sticky"
                    >
                        <Tab eventKey="1" title={('All')}>
                            <UsersTable
                                list={list}
                                loader={loader}
                                page={page}
                                limit={limit}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleSort}
                                onSelect={handleSelect}
                                showSubscriptionPlan
                            />
                        </Tab>

                        <Tab eventKey="2" title={('Active Subscribers')}>
                            <UsersTable
                                list={list}
                                loader={loader}
                                page={page}
                                limit={limit}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleSort}
                                onSelect={handleSelect}
                                showSubscriptionPlan={false}
                            />
                        </Tab>
                    </Tabs>
                </div>

            </div>

            {pagination && pagination.totalCount > limit ? (
                <Pagination
                    totalRecords={pagination.totalCount}
                    pageLimit={limit}
                    pageRangeDisplayed={5}
                    onChangePage={handlePageChange}
                />
            ) : ''}

            <Modal
                show={filterModalOpen}
                onHide={() => setFilterModalOpen(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Filter Users</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="form-group col-md-6">
                            <label>Status</label>
                            <select
                                className="form-control rounded-pill"
                                value={filterDraft.status}
                                onChange={(e) => setFilterDraft({ ...filterDraft, status: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        <div className="form-group col-md-6">
                            <label>Company</label>
                            <input
                                className="form-control rounded-pill"
                                type="text"
                                value={filterDraft.company}
                                onChange={(e) => setFilterDraft({ ...filterDraft, company: e.target.value })}
                                placeholder="Company name"
                            />
                        </div>
                        <div className="form-group col-md-6">
                            <label>User Verified</label>
                            <select
                                className="form-control rounded-pill"
                                value={filterDraft.verifiedUser}
                                onChange={(e) => setFilterDraft({ ...filterDraft, verifiedUser: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="1">Verified</option>
                                <option value="0">Unverified</option>
                            </select>
                        </div>
                        <div className="form-group col-md-6">
                            <label>Email Verified</label>
                            <select
                                className="form-control rounded-pill"
                                value={filterDraft.verifiedEmail}
                                onChange={(e) => setFilterDraft({ ...filterDraft, verifiedEmail: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="1">Verified</option>
                                <option value="0">Unverified</option>
                            </select>
                        </div>
                        <div className="form-group col-md-6">
                            <label>Created From</label>
                            <input
                                className="form-control rounded-pill"
                                type="date"
                                value={filterDraft.createdFrom}
                                onChange={(e) => setFilterDraft({ ...filterDraft, createdFrom: e.target.value })}
                            />
                        </div>
                        <div className="form-group col-md-6">
                            <label>Created To</label>
                            <input
                                className="form-control rounded-pill"
                                type="date"
                                value={filterDraft.createdTo}
                                onChange={(e) => setFilterDraft({ ...filterDraft, createdTo: e.target.value })}
                            />
                        </div>
                        <div className="form-group col-md-6">
                            <label>Updated From</label>
                            <input
                                className="form-control rounded-pill"
                                type="date"
                                value={filterDraft.updatedFrom}
                                onChange={(e) => setFilterDraft({ ...filterDraft, updatedFrom: e.target.value })}
                            />
                        </div>
                        <div className="form-group col-md-6">
                            <label>Updated To</label>
                            <input
                                className="form-control rounded-pill"
                                type="date"
                                value={filterDraft.updatedTo}
                                onChange={(e) => setFilterDraft({ ...filterDraft, updatedTo: e.target.value })}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-link" onClick={resetFilters}>Reset Filters</button>
                    <button type="button" className="btn btn-secondary rounded-pill" onClick={() => setFilterModalOpen(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary rounded-pill" onClick={applyFilters}>Apply Filters</button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={addUserModal}
                onHide={handleCloseAddUser}
                centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleAddUserSubmit}>
                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                className={`form-control ${fullNameErr ? 'is-invalid' : ''} rounded-pill`}
                                type="text"
                                maxLength={80}
                                value={fullName}
                                onChange={(e) => { setFullName(e.target.value); setFullNameErr(''); setAddUserErr(''); }}
                                placeholder="Enter full name" />
                            {fullNameErr ? <span className="error">{fullNameErr}</span> : ''}
                        </div>

                        <div className="form-group">
                            <label>Company Name</label>
                            <input
                                className={`form-control ${companyNameErr ? 'is-invalid' : ''} rounded-pill`}
                                type="text"
                                maxLength={80}
                                value={companyName}
                                onChange={(e) => { setCompanyName(e.target.value); setCompanyNameErr(''); setAddUserErr(''); }}
                                placeholder="Enter company name" />
                            {companyNameErr ? <span className="error">{companyNameErr}</span> : ''}
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                className={`form-control ${userEmailErr ? 'is-invalid' : ''} rounded-pill`}
                                type="email"
                                maxLength={100}
                                value={userEmail}
                                onChange={(e) => { setUserEmail(e.target.value); setUserEmailErr(''); setAddUserErr(''); }}
                                placeholder="Enter email" />
                            {userEmailErr ? <span className="error">{userEmailErr}</span> : ''}
                        </div>

                        <div className="form-group">
                            <label>Password</label>
                            <input
                                className={`form-control ${passwordErr ? 'is-invalid' : ''} rounded-pill`}
                                type="password"
                                maxLength={50}
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setPasswordErr(''); setAddUserErr(''); }}
                                placeholder="Enter password" />
                            {passwordErr ? <span className="error">{passwordErr}</span> : ''}
                        </div>

                        <div className="form-group">
                            <label>Confirm Password</label>
                            <input
                                className={`form-control ${confirmPasswordErr ? 'is-invalid' : ''} rounded-pill`}
                                type="password"
                                maxLength={50}
                                value={confirmPassword}
                                onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordErr(''); setAddUserErr(''); }}
                                placeholder="Confirm password" />
                            {confirmPasswordErr ? <span className="error">{confirmPasswordErr}</span> : ''}
                        </div>

                        <div className="form-group">
                            <label>Country Code</label>
                            <input
                                className="form-control rounded-pill"
                                type="text"
                                maxLength={10}
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                placeholder="e.g. +91" />
                        </div>

                        <div className="form-group">
                            <label>Phone</label>
                            <input
                                className="form-control rounded-pill"
                                type="text"
                                maxLength={15}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Enter phone number" />
                        </div>

                        {addUserErr ? <div className="text-danger mb-2">{addUserErr}</div> : ''}

                        <div className="text-center mt-3">
                            <button
                                disabled={addUserLoading}
                                type="submit"
                                className="btn btn-primary rounded-pill pl-5 pr-5">
                                {addUserLoading ? 'Creating...' : 'Create User'}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            <Modal
                show={createModal}
                onHide={handleClose}
                centered>
                <Modal.Header closeButton>
                    <Modal.Title>Coupon</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Coupon Name</label>

                            <input
                                className={`form-control ${couponIdErr ? 'is-invalid' : ''} rounded-pill`}
                                type="text"
                                maxLength={"40"}
                                value={couponId}
                                onChange={(e) => handleOnChange(e, currentId)}

                                placeholder="Enter coupon name" />
                            {/*//onChange={e => (setCouponId(e.target.value), setCouponIdErr(''))}*/}

                            {couponIdErr ?
                                <OverlayTrigger placement="left" overlay={<Tooltip>{couponIdErr}</Tooltip>}>
                                    <span className="error_tootltip" />
                                </OverlayTrigger> : ''}

                            <span className="error mt-4">
                                {couponIdErr ? couponIdErr : ''}</span>
                        </div>


                        <div className="text-center mt-3">
                            <button disabled={isDisabled || couponIdErr ? true : false} type="submit"
                                className="btn btn-primary rounded-pill pl-5 pr-5">Submit
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

        </div>
    )
}

export default Users;